import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Check, X, Storefront, User } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const STATUS_STYLE = {
  pending: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]",
  approved: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]",
  rejected: "bg-destructive/15 text-destructive",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[status] || ""}`}>
      {status}
    </span>
  );
}

// Approval queue (box=incoming). Admin sees direct customers + resellers;
// a reseller sees its own customers' requests.
export function PaymentsInbox() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/payment-requests", { params: { box: "incoming", status: tab } });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const process = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post(`/billing/payment-requests/${confirm.req.id}/${confirm.action}`);
      toast.success(confirm.action === "approve"
        ? `Approved ${money(confirm.req.amount_minor)} for ${confirm.req.account_name}`
        : "Request rejected");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payments-inbox">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="payments-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="payments-table">
        {loading ? (
          <div className="p-5 text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="payments-empty">
            No {tab} requests.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Requested</TableHead>
                {tab !== "pending" && <TableHead>Processed</TableHead>}
                <TableHead>Note</TableHead>
                {tab === "pending" && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`payment-row-${r.id}`}>
                  <TableCell className="font-medium">{r.account_name || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="gap-1 capitalize">
                      {r.requester_type === "reseller"
                        ? <Storefront className="w-3.5 h-3.5" weight="duotone" />
                        : <User className="w-3.5 h-3.5" weight="duotone" />}
                      {r.requester_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{money(r.amount_minor)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtDate(r.created_at)}</TableCell>
                  {tab !== "pending" && (
                    <TableCell className="text-muted-foreground text-sm">{fmtDate(r.processed_at)}</TableCell>
                  )}
                  <TableCell className="text-muted-foreground text-sm max-w-[220px] truncate">{r.note || "—"}</TableCell>
                  {tab === "pending" && (
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="inline-flex items-center justify-end gap-1">
                        <Button size="sm" className="rounded-lg h-8 w-8 p-0" data-testid={`approve-btn-${r.id}`}
                          onClick={() => setConfirm({ req: r, action: "approve" })}
                          title="Approve" aria-label="Approve">
                          <Check className="w-4 h-4" weight="bold" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" data-testid={`reject-btn-${r.id}`}
                          onClick={() => setConfirm({ req: r, action: "reject" })}
                          title="Reject" aria-label="Reject">
                          <X className="w-4 h-4" weight="bold" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="payment-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === "approve" ? "Approve payment request?" : "Reject payment request?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "approve"
                ? `This will credit ${money(confirm?.req?.amount_minor)} to ${confirm?.req?.account_name}'s cash balance and record a transaction. This cannot be undone.`
                : `This will reject ${confirm?.req?.account_name}'s request for ${money(confirm?.req?.amount_minor)}. No balance change.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-process-btn" onClick={(e) => { e.preventDefault(); process(); }} disabled={busy}>
              {busy ? "Processing…" : (confirm?.action === "approve" ? "Approve" : "Reject")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
